import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@anonymous/store'
import { actionTypes } from '@base/store/store-types'
import { getToken } from '@base/store'
import {
  ADMIN_SECTION_PATH,
  DJANGO_ADMIN_PATH_NAME,
  mobileAppScheme,
  useMobileAppScheme,
  USER_SECTION_PATH,
} from '@base/configs'

import NotFoundPage from '@base/components/NotFoundPage.vue'
import BasePage from '@anonymous/views/BasePage.vue'
import Home from '@anonymous/components/Home.vue'
import AdminLogin from '@anonymous/components/admin/AdminLogin.vue'
import AdminResetPassword
  from '@anonymous/components/admin/AdminResetPassword.vue'
import AdminConfirmResetPassword
  from '@anonymous/components/admin/AdminConfirmResetPassword.vue'
import AdminEmailConfirm
  from '@anonymous/components/admin/AdminEmailConfirm.vue'
import AnonUserView from '@anonymous/views/AnonUserView.vue'
import CreateUser from '@anonymous/components/CreateUser.vue'
import CreateMarket from '@anonymous/components/CreateMarket.vue'

import VueRouterMiddleware, {
  createMiddleware,
  middleware
} from 'vue-router-middleware'
import InstagramMediaView from '@base/views/InstagramMediaView'
import CreateBookingView from "@base/views/CreateBookingView.vue";
import MarketTermsView from "@base/views/MarketTermsView.vue";
import {
  canContinueBookingRequestExtension, canContinueFinalizeBookingPaymentRequest,
  canExtendBookingRequest,
  canFinalizeBookingPaymentRequest
} from "@base/utils";
import ExtendBookingView from "@base/views/ExtendBookingView.vue";
import BookingPaymentRequestView from "@base/views/BookingPaymentRequestView.vue";
import i18n from "@base/i18n";


// Set market_id header to axios from local storage
const checkTokenStorage = (next, tokenName) => {
  const token = getToken(tokenName)
  if (token && token.isAdmin)
    window.location.replace(ADMIN_SECTION_PATH)
  else if (token && !token.isAdmin)
    window.location.replace(USER_SECTION_PATH)
  else
    next()
}

const checkDeepLink = (to) => {
  // If user opens web app via link from email (or by another way of deep link)
  // then we check if he does this from mobile - redirect him to mobile app
  if (useMobileAppScheme && (navigator.userAgent.toLowerCase().indexOf('android') > -1
      || navigator.userAgent.toLowerCase().indexOf('iphone') > -1)) {
    window.location.href = `${mobileAppScheme}${to.path}${Object.values(to.query).join('/')}`
  }
}

const checkOnlineBookingFeature = (marketId, next, checkElavonAPIValid = true) => {
  store.dispatch(actionTypes.GET_ANON_MARKET, marketId)
    .then(() => {
      if (store.state.anonMarket) {
        if (checkElavonAPIValid) {
          if (!store.state.anonMarket.marketId || !store.state.anonMarket.onlineBookingFeature || !store.state.anonMarket.elavonApiValid || !store.state.anonMarket.showPaymentTerms)
            next({name: 'not-found'})
          else
            next()
        } else {
          if (!store.state.anonMarket.marketId || !store.state.anonMarket.onlineBookingFeature || !store.state.anonMarket.showPaymentTerms)
            next({name: 'not-found'})
          else
            next()
        }
      }
      else
        next({ name: 'not-found' })
    })
    .catch(() => {
      next({ name: 'not-found' })
    })
}

createMiddleware('check-market-id', (args, to, from, next) => {
  store.dispatch(actionTypes.GET_ANON_MARKET, to.params.marketId)
    .then(() => {
      if (store.state.anonMarket) {
        if (!store.state.anonMarket.marketId)
          next({ name: 'not-found' })
        else
          next()
      }
      else
        next({ name: 'not-found' })
    })
    .catch(() => {
      next({ name: 'not-found' })
    })
})

createMiddleware('check-market-online-booking-feature', (args, to, from, next) => {
  checkOnlineBookingFeature(to.params.marketId, next)
})

createMiddleware('check-media-id', (args, to, from, next) => {
  store.dispatch(actionTypes.GET_MARKET_INSTAGRAM_MEDIA, to.params.mediaId)
    .then(() => {
      if (store.state.marketInstagramMedia) {
        if (!store.state.marketInstagramMedia.mediaId)
          next({ name: 'not-found' })
        else
          next()
      }
      else
        next({ name: 'not-found' })
    })
    .catch(() => {
      next({ name: 'not-found' })
    })
})

createMiddleware('check-extension-booking-request-id', (args, to, from, next) => {
  store.dispatch(actionTypes.GET_BOOKING_REQUEST_ANON, { bookingRequestId: to.params.bookingRequestId, queryPayload: to.query })
    .then(() => {
      if (store.state.selectedBookingRequest) {
        if (!store.state.selectedBookingRequest.id) {
          Vue.notify({
            group: 'app',
            type : 'error',
            title: i18n.t('notifications.title.error'),
            text: i18n.t('notifications.text.booking_requests.error_extend_booking_request_invalid_link')
          })
          next({name: 'not-found'})
        } else if (store.state.selectedBookingRequest.status === 'expired'
          || (!canExtendBookingRequest(store.state.selectedBookingRequest)
          && !canContinueBookingRequestExtension(store.state.selectedBookingRequest))) {
          // Try to redirect to an active child link if there's any
          if (store.state.selectedBookingRequest.lastActiveChildId) {
            next({name: 'extend-booking-request', params: { bookingRequestId: store.state.selectedBookingRequest.lastActiveChildId }, query: to.query})
          } else {
            Vue.notify({
              group: 'app',
              type: 'error',
              title: i18n.t('notifications.title.error'),
              text: i18n.t('notifications.text.booking_requests.error_booking_request_already_extended')
            })
            next({name: 'user-login', params: { marketId: store.state.selectedBookingRequest.marketId }})
          }
        } else {
          checkOnlineBookingFeature(store.state.selectedBookingRequest.marketId, next)
        }
      }
      else {
        Vue.notify({
          group: 'app',
          type : 'error',
          title: i18n.t('notifications.title.error'),
          text: i18n.t('notifications.text.booking_requests.error_extend_booking_request_invalid_link')
        })
        next({name: 'not-found'})
      }
    })
    .catch(() => {
      Vue.notify({
        group: 'app',
        type : 'error',
        title: i18n.t('notifications.title.error'),
        text: i18n.t('notifications.text.booking_requests.error_extend_booking_request_invalid_link')
      })
      next({ name: 'not-found' })
    })
})

createMiddleware('check-booking-payment-request-id', (args, to, from, next) => {
  store.dispatch(actionTypes.GET_BOOKING_REQUEST_ANON, { bookingRequestId: to.params.bookingRequestId, queryPayload: to.query })
    .then(() => {
      if (store.state.selectedBookingRequest) {
        if (!store.state.selectedBookingRequest.id) {
          Vue.notify({
            group: 'app',
            type : 'error',
            title: i18n.t('notifications.title.error'),
            text: i18n.t('notifications.text.booking_requests.error_payment_request_invalid_link')
          })
          next({name: 'not-found'})
        } else if (!store.state.selectedBookingRequest.isPaymentRequest
            || (!canFinalizeBookingPaymentRequest(store.state.selectedBookingRequest)
                && !canContinueFinalizeBookingPaymentRequest(store.state.selectedBookingRequest))) {
          if (store.state.selectedBookingRequest.lastActiveChildId) {
            next({name: 'booking-payment-request', params: { bookingRequestId: store.state.selectedBookingRequest.lastActiveChildId }, query: to.query})
          } else {
            Vue.notify({
              group: 'app',
              type: 'error',
              title: i18n.t('notifications.title.error'),
              text: i18n.t('notifications.text.booking_requests.error_payment_request_already_paid')
            })
            next({name: 'user-login', params: {marketId: store.state.selectedBookingRequest.marketId}})
          }
        } else {
          checkOnlineBookingFeature(store.state.selectedBookingRequest.marketId, next)
        }
      }
      else {
        Vue.notify({
          group: 'app',
          type : 'error',
          title: i18n.t('notifications.title.error'),
          text: i18n.t('notifications.text.booking_requests.error_payment_request_invalid_link')
        })
        next({name: 'not-found'})
      }
    })
    .catch(() => {
      Vue.notify({
        group: 'app',
        type : 'error',
        title: i18n.t('notifications.title.error'),
        text: i18n.t('notifications.text.booking_requests.error_payment_request_invalid_link')
      })
      next({ name: 'not-found' })
    })
})

const routes = [
  {
    path: '/404',
    name: 'not-found',
    component: NotFoundPage,
  },
  {
    path: '/',
    component: BasePage,
    children: [
      {
        path: '',
        name: 'base',
        component: Home,
      },
      {
        path: DJANGO_ADMIN_PATH_NAME,
        beforeEnter:() => {
          location.replace('/' + DJANGO_ADMIN_PATH_NAME)
        }
      },
      {
        path: 'login',
        name: 'admin-login',
        beforeEnter: (to, from, next) => {
          checkTokenStorage(next, 'AdminToken')
        },
        component: AdminLogin,
      },
      {
        path: 'reset_password',
        name: 'admin-reset-password',
        component: AdminResetPassword,
      },
      {
        path: 'confirm_email',
        beforeEnter: (to, from, next) => {
          checkTokenStorage(next, 'AdminToken')
        },
        name: 'admin-confirm-email',
        component: AdminEmailConfirm,
      },
      {
        path: 'confirm_reset_password',
        beforeEnter: (to, from, next) => {
          checkTokenStorage(next, 'AdminToken')
        },
        name: 'admin-confirm-reset-password',
        component: AdminConfirmResetPassword,
      },
      {
        path: 'create_new_market',
        name: 'create-new-market',
        component: CreateUser,
      },
      {
        path: 'register_market',
        name: 'register-market',
        component: CreateMarket,
      },
      {
        path: ':marketId/market_terms',
        name: 'market-terms',
        beforeEnter: (to, from, next) => {
          checkOnlineBookingFeature(to.params.marketId, next, false)
        },
        component: MarketTermsView,
      },
      ...middleware('check-market-online-booking-feature', [
        {
          path: ':marketId/create_booking',
          name: 'create-booking',
          beforeEnter: (to, from, next) => {
            checkTokenStorage(next, 'CustomerToken')
          },
          component: CreateBookingView,
          props: { isCustomerBooking: false },
        },
      ]),
      ...middleware('check-extension-booking-request-id', [
          {
            path: 'extend_booking_request/:bookingRequestId',
            name: 'extend-booking-request',
            component: ExtendBookingView,
            props: { isCustomerView: false },
          },
        ]
      ),
      ...middleware('check-booking-payment-request-id', [
        {
          path: 'booking_payment_request/:bookingRequestId',
          name: 'booking-payment-request',
          component: BookingPaymentRequestView,
          props: { isCustomerView: false },
        },
      ]),
      ...middleware('check-market-id', [
        {
          path: ':marketId',
          name: 'user',
          redirect: { name: 'user-login' },
        },
        {
          path: 'market/loppeonline/:marketId',
          name: 'market-user',
          redirect: { name: 'user-login' },
        },
        {
          path: 'market/loppeonline_global/:marketId',
          name: 'market-user-global',
          redirect: { name: 'user-login' },
        },
        {
          path: ':marketId/login',
          name: 'user-login',
          beforeEnter: (to, from, next) => {
            checkTokenStorage(next, 'CustomerToken')
          },
          component: AnonUserView,
          props: { componentName: 'UserLogin' }
        },
        {
          path: ':marketId/reset_password',
          name: 'user-reset-password',
          component: AnonUserView,
          props: { componentName: 'UserResetPassword' }
        },
        {
          path: ':marketType/:marketId/confirm_reset_password',
          name: 'user-confirm-reset-password',
          beforeEnter: (to, from, next) => {
            checkTokenStorage(next, 'CustomerToken')
            checkDeepLink(to)
          },
          component: AnonUserView,
          props: { componentName: 'UserConfirmResetPassword' }
        },
        {
          path: ':marketType/:marketId/confirm_email',
          beforeEnter: (to, from, next) => {
            checkTokenStorage(next, 'CustomerToken')
            checkDeepLink(to)
          },
          name: 'user-confirm-email',
          component: AnonUserView,
          props: { componentName: 'UserEmailConfirm' }
        },
      ]),
      ...middleware('check-media-id', [
        {
          path: 'post/loppeonline/:mediaId',
          name: 'media-page',
          component: InstagramMediaView,
        },
      ]),
    ]
  },
  {
    path: '*',
    redirect: { name: 'not-found' }
  }
]

const router = new VueRouter({ mode: 'history', routes })

Vue.use(VueRouter)
Vue.use(VueRouterMiddleware, { router })

export default router
